import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { getImageUrl } from '../utils/imageUrl';
import { trackEvent } from '../utils/analytics';
import api from '../services/api';
import SEO from '../components/SEO';
import { loadRecaptchaScript, executeRecaptcha } from '../services/recaptcha';
import LocationPicker from '../components/LocationPicker';
import PaymentStep from '../components/PaymentStep';
import IuteInstalments from '../components/IuteInstalments';
import useIuteOffer from '../hooks/useIuteOffer';
import { loadIutePay } from '../utils/iutepay';
import { formatPrice } from '../utils/format';
import { Button } from '../components/primitives';
import { Icon, ICON_DEFAULTS } from '../utils/icons';
import { currentAttribution } from '../utils/attribution';
import './Checkout.css';

// Checkout fields in the order they appear, for moving focus to the first
// one with a problem.
const FIELD_ORDER = ['name', 'email', 'phone', 'whatsappConsent', 'street', 'city', 'postalCode', 'country', 'acceptTerms'];

// The checkout saved for the payment in progress (id + its payment token),
// so a retry, a refresh or "Back" releases it instead of holding the stock
// twice. The server lets an unpaid checkout go after 30 minutes anyway.
const ATTEMPT_KEY = 'checkoutAttempt';
const savedAttempt = () => {
  try { return JSON.parse(sessionStorage.getItem(ATTEMPT_KEY) || 'null'); } catch { return null; }
};
const rememberAttempt = (id, paymentToken) => {
  try {
    sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ id, paymentToken }));
    // services/api.js sends this as X-Payment-Token on /api/payment/*.
    sessionStorage.setItem('paymentToken', paymentToken);
    sessionStorage.setItem('paymentTokenOrderId', String(id));
  } catch { /* storage blocked (private window): the server's hold still ends it */ }
};
const forgetAttempt = () => {
  try {
    sessionStorage.removeItem(ATTEMPT_KEY);
    sessionStorage.removeItem('paymentToken');
    sessionStorage.removeItem('paymentTokenOrderId');
  } catch { /* ignore */ }
};
const releaseEarlierAttempt = async () => {
  const attempt = savedAttempt();
  if (!attempt?.id) return;
  forgetAttempt();
  await api.post('/api/payment/abandon', { orderId: attempt.id }, { headers: { 'X-Payment-Token': attempt.paymentToken || '' } })
    .catch(() => {}); // already gone or placed: nothing to release
};

// Methods paid on the spot (the order is placed at once) and their confirm endpoints.
const CONFIRM_ENDPOINT = { cod: '/api/payment/cod/confirm', bank_transfer: '/api/payment/bank-transfer/confirm' };

const Checkout = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { cart, getCartSubtotal, calculateTax, calculateTotal, getEffectiveShipping, clearCart, siteSettings } = useCart();
  const { customer, isAuthenticated } = useCustomerAuth();
  const { minOrderAmount, currency: ctxCurrency, currencyLabel, returnsEnabled, returnsWindowDays, storeDefaultCountry, storeDefaultPhonePrefix, deliveryCountries, whatsappNotifications } = useSiteSettings();
  const currency = currencyLabel || ctxCurrency || t('common.currency');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formStartTime] = useState(() => Math.floor(Date.now() / 1000)); // Track when form was loaded
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  // Load reCAPTCHA script on component mount
  useEffect(() => {
    loadRecaptchaScript()
      .then(() => setRecaptchaReady(true))
      .catch(() => setRecaptchaReady(true));
  }, []);

  // GA4 begin_checkout — fires once when the checkout page first
  // mounts with a non-empty cart. Empty-cart visits are redirected
  // away below, so we guard on cart.length to avoid the false signal
  // from the redirect frame.
  useEffect(() => {
    if (cart.length === 0) return;
    trackEvent('begin_checkout', {
      currency: ctxCurrency || 'USD',
      value: parseFloat(getCartSubtotal()) || 0,
      items: cart.map(item => ({
        item_id: item.sku || String(item.id),
        product_id: item.id,
        item_name: item.name,
        price: parseFloat(item.price) || 0,
        quantity: item.quantity,
      })),
    });
    // Intentionally fires only on mount + on cart-empties-to-non-empty
    // transitions. cart object reference would refire too often.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length === 0]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    postalCode: '',
    country: '', // Populated from store_default_country once settings load (see useEffect below)
    notes: '',
    latitude: null,
    longitude: null,
    website: '' // Honeypot field - should remain empty
  });

  // Fill the country field from the operator-configured default once
  // settings load — never overwrites a value the buyer already typed.
  useEffect(() => {
    if (!storeDefaultCountry) return;
    setFormData(prev => ({ ...prev, country: prev.country || storeDefaultCountry }));
  }, [storeDefaultCountry]);

  const [formErrors, setFormErrors] = useState({});
  // 'payment' only for card and PayPal, which need their own form.
  const [checkoutStep, setCheckoutStep] = useState('form'); // 'form' | 'payment'
  const [createdOrder, setCreatedOrder] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [couponResult, setCouponResult] = useState(null);
  const [automaticCoupon, setAutomaticCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [cartSaved, setCartSaved] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  // Phone-first guests: when email is left empty, explicit WhatsApp consent
  // is required so the customer still receives order updates somewhere.
  const [whatsappConsent, setWhatsappConsent] = useState(false);
  const [activeBundles, setActiveBundles] = useState([]);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [locationDetectError, setLocationDetectError] = useState('');

  // The shopper picks how to pay once, on this form. Pay on delivery (and
  // bank transfer) place the order at once; IutePay opens IUTE's window
  // from here and the order is placed once the application is submitted;
  // card and PayPal open their payment form next. Until then the server
  // keeps the checkout as "awaiting payment", not as an order.
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentChoice, setPaymentChoice] = useState(null);
  // A payment that didn't go through, shown next to the button the shopper just used.
  const [paymentError, setPaymentError] = useState(null);

  useEffect(() => {
    api.get('/api/settings/payment')
      .then(res => setPaymentConfig(res.data))
      .catch(() => setPaymentConfig(null)); // null → classic 2-step flow
  }, []);

  // PHASE 9 — once the buyer has typed a delivery city, look up the real
  // city-specific shipping rate so the displayed total matches what the
  // server will actually charge, rather than only the generic "before we
  // know the city" default. Debounced so this doesn't fire on every
  // keystroke; the order-creation endpoint remains authoritative regardless
  // — this is purely a pre-submit estimate improvement.
  const [cityShippingRate, setCityShippingRate] = useState(null);
  useEffect(() => {
    const city = formData.city.trim();
    if (!city) {
      setCityShippingRate(null);
      return;
    }
    const timeout = setTimeout(() => {
      api.get('/api/shipping/rate', { params: { city } })
        .then(res => setCityShippingRate(res.data))
        .catch(() => setCityShippingRate(null));
    }, 400);
    return () => clearTimeout(timeout);
  }, [formData.city]);

  useEffect(() => {
    if (isAuthenticated && customer) {
      setFormData(prev => ({
        ...prev,
        name: customer.name || prev.name,
        email: customer.email || prev.email,
        phone: customer.phone || prev.phone,
      }));
      const token = localStorage.getItem('customer_token');
      if (token) {
        api.get('/api/customer/addresses/latest', { headers: { Authorization: `Bearer ${token}` } })
          .then(res => {
            if (res.data) {
              setFormData(prev => ({
                ...prev,
                street: prev.street || res.data.street || '',
                city: prev.city || res.data.city || '',
                postalCode: prev.postalCode || res.data.postalCode || '',
                country: res.data.country || prev.country,
                latitude: res.data.latitude || prev.latitude,
                longitude: res.data.longitude || prev.longitude,
              }));
            }
          })
          .catch(() => {});
      }
    }
  }, [isAuthenticated, customer]);

  // Pre-fill form from last guest order — only for unauthenticated users.
  // Authenticated users already get address from the API call above.
  useEffect(() => {
    if (isAuthenticated) return;
    try {
      const raw = localStorage.getItem('guest_checkout_data');
      if (!raw) return;
      const saved = JSON.parse(raw);
      setFormData(prev => ({
        ...prev,
        name: saved.name || prev.name,
        email: saved.email || prev.email,
        phone: saved.phone || prev.phone,
        street: saved.street || prev.street,
        city: saved.city || prev.city,
        postalCode: saved.postalCode || prev.postalCode,
        country: saved.country || prev.country,
      }));
    } catch (_) {
      // Malformed JSON or unavailable storage — silently skip
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    api.get('/api/bundles/active').then(r => setActiveBundles(r.data || [])).catch(() => {});
  }, []);

  const matchedBundles = activeBundles.filter(bundle => {
    const requiredItems = bundle.items.filter(bi => !bi.isFreeItem);
    return requiredItems.every(bi =>
      cart.some(ci => ci.id === bi.productId && ci.quantity >= bi.quantity)
    );
  }).map(bundle => {
    const freeItems = bundle.items.filter(bi => bi.isFreeItem);
    let savings = 0;
    if (bundle.type === 'bogo' && freeItems.length > 0) {
      savings = freeItems.reduce((s, fi) => s + parseFloat(fi.productPrice) * fi.quantity, 0);
    } else if (bundle.type === 'percent_off') {
      const bundleTotal = bundle.items.reduce((s, bi) => s + parseFloat(bi.productPrice) * bi.quantity, 0);
      savings = bundleTotal * (parseFloat(bundle.discountValue) / 100);
    } else if (bundle.type === 'fixed_off') {
      savings = parseFloat(bundle.discountValue);
    }
    return { ...bundle, savings };
  }).filter(b => b.savings > 0);

  const fmtPrice = (v) => formatPrice(v, { lang: i18n.language });

  const subtotal = getCartSubtotal();
  // Once a city-specific rate has resolved, apply ITS cost/threshold
  // instead of the generic default estimate — same free-shipping check
  // getEffectiveShipping does, just against the city's own numbers.
  const shippingCost = cityShippingRate
    ? (() => {
        const threshold = parseFloat(cityShippingRate.freeShippingThreshold) || 0;
        if (threshold > 0 && subtotal >= threshold) return '0.00';
        return cityShippingRate.shippingCost;
      })()
    : getEffectiveShipping(subtotal);
  const tax = calculateTax(subtotal);
  const total = calculateTotal(subtotal, shippingCost);
  const taxLabel = siteSettings.taxIncludedInPrice
    ? t('common.taxIncluded', 'Tax (included)')
    : `${t('common.tax', 'Tax')} (${siteSettings.taxRate}%)`;

  useEffect(() => {
    let alive = true;
    if (cart.length === 0 || (couponResult && !couponResult.combinable)) {
      setAutomaticCoupon(null);
      return () => { alive = false; };
    }
    Promise.resolve(api.post('/api/coupons/automatic', {
      email: formData.email || null,
      shippingCost,
      requireCombinable: Boolean(couponResult?.combinable),
      items: cart.map(item => ({ productId: item.id, quantity: item.quantity })),
    })).then(response => { if (alive) setAutomaticCoupon(response?.data?.coupon || null); })
      .catch(() => { if (alive) setAutomaticCoupon(null); });
    return () => { alive = false; };
  }, [couponResult, cart, formData.email, shippingCost]);

  const discountAmount = (couponResult ? parseFloat(couponResult.discount) : 0) + (automaticCoupon ? parseFloat(automaticCoupon.discount) : 0);
  const waivedShipping = (couponResult?.freeShipping || automaticCoupon?.freeShipping) ? parseFloat(shippingCost) : 0;
  const adjustedTotal = Math.max(0, parseFloat(total) - discountAmount - waivedShipping);
  const payableTotal = discountAmount > 0 || waivedShipping > 0 ? adjustedTotal : parseFloat(total);
  // IutePay only when it's on and IUTE finances this total.
  const iuteMonthlyPayment = useIuteOffer(paymentConfig?.iutepayEnabled && paymentConfig?.iutepayPublicKey ? payableTotal : 0);
  const iutepayUsable = Boolean(iuteMonthlyPayment);

  // Methods offered for this cart, in display order; the shopper's pick,
  // else the store's default, else the first.
  const paymentMethods = [
    paymentConfig?.codEnabled !== false && 'cod',
    iutepayUsable && 'iutepay',
    paymentConfig?.stripeEnabled && 'stripe',
    paymentConfig?.paypalEnabled && 'paypal',
    paymentConfig?.bankTransferEnabled && 'bank_transfer',
  ].filter(Boolean);
  const paymentMethod = paymentMethods.includes(paymentChoice)
    ? paymentChoice
    : (paymentMethods.includes(paymentConfig?.defaultPaymentMethod) ? paymentConfig.defaultPaymentMethod : (paymentMethods[0] || 'cod'));

  const minOrder = parseFloat(minOrderAmount) || 0;
  const belowMinOrder = minOrder > 0 && parseFloat(subtotal) < minOrder;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidatingCoupon(true);
    setCouponError('');
    setCouponResult(null);
    try {
      const res = await api.post('/api/coupons/validate', { code: couponCode.trim(), subtotal: parseFloat(subtotal) });
      setCouponResult(res.data);
    } catch (err) {
      setCouponError(err.response?.data?.error || 'Invalid coupon code');
    } finally {
      setValidatingCoupon(false);
    }
  };

  const saveAbandonedCart = useCallback(() => {
    if (!formData.email || cart.length === 0 || cartSaved) return;
    const s = getCartSubtotal();
    api.post('/api/abandoned-carts', {
      email: formData.email,
      name: formData.name || null,
      items: cart.map(item => ({ id: item.id, name: item.name, quantity: item.quantity, price: item.price })),
      subtotal: s,
    }).catch(() => {});
    setCartSaved(true);
  }, [formData.email, formData.name, cart, cartSaved, getCartSubtotal]);

  useEffect(() => {
    const handleBeforeUnload = () => saveAbandonedCart();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveAbandonedCart]);

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponResult(null);
    setCouponError('');
  };

  const handleDetectLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationDetectError(t('checkout.locationError', 'Could not detect location. Please fill in manually.'));
      return;
    }
    setDetectingLocation(true);
    setLocationDetectError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude: lat, longitude: lng } = pos.coords;
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=${i18n.language || 'en'}`,
            { headers: { 'Accept': 'application/json' } }
          );
          const data = await res.json();
          const addr = data.address || {};
          const street = [addr.road, addr.house_number].filter(Boolean).join(' ') || '';
          const city = addr.city || addr.town || addr.village || addr.municipality || '';
          const postalCode = addr.postcode || '';
          // Map ISO country code to the dropdown value used in the form
          const countryMap = {
            us: 'United States', ca: 'Canada', gb: 'United Kingdom', au: 'Australia',
            al: 'Albania', xk: 'Kosovo', mk: 'North Macedonia',
            rs: 'Serbia', me: 'Montenegro', ba: 'Bosnia and Herzegovina',
            hr: 'Croatia', bg: 'Bulgaria', gr: 'Greece', it: 'Italy',
            de: 'Germany', at: 'Austria', fr: 'France', es: 'Spain',
          };
          const countryCode = (addr.country_code || '').toLowerCase();
          const country = countryMap[countryCode] || addr.country || '';
          setFormData(prev => ({
            ...prev,
            ...(street && !prev.street ? { street } : {}),
            ...(city && !prev.city ? { city } : {}),
            ...(postalCode && !prev.postalCode ? { postalCode } : {}),
            ...(country && (!deliveryCountries?.length || deliveryCountries.includes(country)) ? { country } : {}),
            latitude: lat,
            longitude: lng,
          }));
        } catch {
          setLocationDetectError(t('checkout.locationError', 'Could not detect location. Please fill in manually.'));
        } finally {
          setDetectingLocation(false);
        }
      },
      () => {
        setLocationDetectError(t('checkout.locationError', 'Could not detect location. Please fill in manually.'));
        setDetectingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [t, i18n.language, deliveryCountries]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) errors.name = t('checkout.errors.fillAllFields');
    // Email is optional only while WhatsApp can carry order updates; then,
    // when omitted, the WhatsApp consent checkbox must be ticked.
    if (formData.email.trim()) {
      if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = t('checkout.errors.invalidEmail');
      }
    } else if (!whatsappNotifications) {
      errors.email = t('checkout.errors.emailRequired', 'Enter your email so we can send you order updates.');
    } else if (!whatsappConsent) {
      errors.whatsappConsent = t('checkout.errors.whatsappConsentRequired', 'Add an email or agree to receive order updates on WhatsApp.');
    }
    if (!formData.phone.trim()) errors.phone = t('checkout.errors.fillAllFields');
    if (!formData.street.trim()) errors.street = t('checkout.errors.fillAllFields');
    if (!formData.city.trim()) errors.city = t('checkout.errors.fillAllFields');
    if (!formData.postalCode.trim()) errors.postalCode = t('checkout.errors.fillAllFields');
    if (!formData.country.trim()) errors.country = t('checkout.errors.fillAllFields');

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) focusFirstInvalid(Object.keys(errors));
    return Object.keys(errors).length === 0;
  };

  // After a failed submit, take the shopper to the first field that needs
  // fixing (in the order they appear) instead of leaving focus on the
  // button, so screen readers announce it and phones scroll to it.
  const focusFirstInvalid = (fields) => {
    const first = FIELD_ORDER.find((field) => fields.includes(field));
    if (!first) return;
    window.requestAnimationFrame(() => {
      const el = document.getElementById(first);
      if (!el) return;
      el.scrollIntoView?.({ block: 'center', behavior: 'auto' });
      el.focus({ preventScroll: true });
    });
  };

  const paymentLabel = (method) => ({
    cod: t('payment.cod', 'Cash on Delivery'),
    iutepay: t('checkout.payment.iutepay', 'Pay in instalments with IutePay'),
    stripe: t('payment.creditCard', 'Credit / Debit Card'),
    paypal: 'PayPal',
    bank_transfer: t('payment.bankTransfer', 'Bank Transfer'),
  }[method]);
  const paymentHint = (method) => ({
    cod: t('payment.codHint', 'Pay when you receive your order'),
    iutepay: t('checkout.payment.iutepayFrom', { amount: fmtPrice(iuteMonthlyPayment), currency, defaultValue: `From ${fmtPrice(iuteMonthlyPayment)} ${currency} a month with IuteCredit` }),
    stripe: 'Visa, Mastercard, Amex',
    paypal: t('payment.paypalHint', 'Pay with your PayPal account'),
    bank_transfer: t('payment.bankTransferHint', 'Transfer the amount manually using the account details shown next'),
  }[method]);
  const submitLabel = {
    cod: t('checkout.placeOrderCod', 'Order Now — Pay on Delivery'),
    iutepay: t('payment.iutepayPay', 'Continue with IutePay'),
    bank_transfer: t('checkout.placeOrderBankTransfer', 'Place order — pay by bank transfer'),
  }[paymentMethod] || t('checkout.continueToPayment', 'Continue to Payment');

  // The checkout is an order now: empty the cart and show the confirmation.
  const finishOrder = (order, method) => {
    clearCart();
    forgetAttempt();
    const email = formData.email.trim();
    if (email) {
      api.post('/api/abandoned-carts/recover', { email }).catch(() => {});
    }
    navigate(`/order-confirmation/${order.orderNumber}`, {
      state: { order: { ...order, status: 'pending', paymentMethod: method } },
    });
  };

  // IUTE's window opens over the checkout. Submitting the application
  // places the order; closing the window or a refusal leaves the shopper
  // here to try again or pick another method.
  const payWithIute = async (order) => {
    const { data } = await api.post('/api/payment/iutepay/begin', { orderId: order.id });
    if (data.paymentToken) rememberAttempt(order.id, data.paymentToken);
    const iute = await loadIutePay({ apiBase: data.apiBase, publicKey: data.publicKey, lang: (i18n.language || 'sq').slice(0, 2) });
    await new Promise((resolve) => {
      iute.checkout(data.payload, {
        onSuccess: async (result) => {
          try {
            await api.post('/api/payment/iutepay/confirm', { orderId: order.id, applicationRef: result?.checkoutSessionId || null });
            finishOrder(order, 'iutepay');
          } catch (err) {
            setPaymentError(err.response?.data?.error || t('checkout.confirmFailed', 'We could not confirm your order. Please try again.'));
          }
          resolve();
        },
        onFailure: (result) => {
          setPaymentError(result?.message || t('checkout.iutepayNotCompleted', 'The IutePay application was not completed. You can try again or choose another payment method.'));
          resolve();
        },
      });
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = paymentMethod;

    if (!validateForm()) {
      setError(t('checkout.errors.fillAllFields'));
      return;
    }

    if (!termsAccepted) {
      setTermsError(true);
      focusFirstInvalid(['acceptTerms']);
      setError(t('checkout.termsRequired', 'Please accept the Terms & Conditions and Privacy Policy to continue.'));
      return;
    }
    setTermsError(false);

    if (cart.length === 0) {
      setError(t('checkout.errors.emptyCart'));
      return;
    }

    setLoading(true);
    setError(null);
    setPaymentError(null);

    try {
      // Execute reCAPTCHA before submitting
      let recaptchaToken = null;
      if (recaptchaReady) {
        try {
          recaptchaToken = await executeRecaptcha('checkout');
        } catch {
          // reCAPTCHA will be validated server-side
        }
      }

      const trimmedEmail = formData.email.trim();
      const orderData = {
        client: {
          name: formData.name,
          email: trimmedEmail || null,
          phone: formData.phone
        },
        notificationChannel: trimmedEmail ? 'email' : 'whatsapp',
        deliveryAddress: {
          street: formData.street,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country,
          latitude: formData.latitude,
          longitude: formData.longitude
        },
        items: cart.map(item => ({
          productId: item.id,
          quantity: item.quantity,
          ...(item.variantId ? { variantId: item.variantId } : {}),
        })),
        notes: formData.notes,
        couponCode: couponResult?.code || null,
        attribution: currentAttribution(),
        website: formData.website,
        formStartTime: formStartTime,
        recaptchaToken: recaptchaToken,
        language: i18n.language,  // stored on Order.locale → emails sent in customer's language
        paymentMethod: method,
      };

      // A checkout from an earlier try (closed IUTE window, refresh) gives
      // its stock back before the new one takes it.
      await releaseEarlierAttempt();
      const response = await api.post('/api/orders', orderData);
      const order = response.data;
      if (order?.paymentToken) rememberAttempt(order.id, order.paymentToken);
      setCreatedOrder(order);

      if (CONFIRM_ENDPOINT[method]) {
        try {
          await api.post(CONFIRM_ENDPOINT[method], { orderId: order.id });
        } catch (err) {
          setPaymentError(err.response?.data?.error || t('checkout.confirmFailed', 'We could not confirm your order. Please try again.'));
          return;
        }
        finishOrder(order, method);
        return;
      }

      if (method === 'iutepay') {
        try {
          await payWithIute(order);
        } catch {
          setPaymentError(t('payment.iutepayUnavailable', 'IutePay is unavailable right now. Please choose another payment method.'));
        }
        return;
      }

      setCheckoutStep('payment'); // card or PayPal
    } catch (err) {
      // 409 + requireLogin: the email already belongs to a passworded
      // account, so we redirect to login instead of silently re-using or
      // overwriting that customer's row. The server returns the canonical
      // login URL but we fall back to a sensible default.
      if (err.response?.status === 409 && err.response?.data?.requireLogin) {
        const target = err.response.data.loginUrl || '/login?redirect=/checkout';
        navigate(target);
        return;
      }
      setError(err.response?.data?.error || 'Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentComplete = (method) => finishOrder(createdOrder, method);

  // Back from the card / PayPal form: that checkout's stock goes back.
  const handlePaymentBack = () => {
    releaseEarlierAttempt();
    setCheckoutStep('form');
  };

  if (cart.length === 0 && !loading && !createdOrder) {
    return (
      <div className="checkout-page container">
        <SEO title={t('checkout.title', 'Checkout')} />
        <div className="empty-cart-message">
          <Icon.Cart size={48} strokeWidth={1.25} className="empty-icon" aria-hidden="true" />
          <h2>{t('cart.empty')}</h2>
          <p>{t('cart.emptyMessage')}</p>
          <Button variant="brand" onClick={() => navigate('/products')}>
            {t('cart.shopNow')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page container">
      <SEO title={t('checkout.title')} />
      
      <h1 className="checkout-title">
        {t('checkout.title')}
      </h1>

      <div className="checkout-progress">
        <div className={`checkout-progress-step ${checkoutStep === 'form' ? 'active' : 'completed'}`}>
          <span className="checkout-progress-dot">1</span>
          <span>{t('checkout.stepInformation', 'Information')}</span>
        </div>
        <div className={`checkout-progress-line ${checkoutStep === 'payment' ? 'filled' : ''}`} />
        <div className={`checkout-progress-step ${checkoutStep === 'payment' ? 'active' : ''}`}>
          <span className="checkout-progress-dot">2</span>
          <span>{t('checkout.stepPayment', 'Payment')}</span>
        </div>
        <div className="checkout-progress-line" />
        <div className="checkout-progress-step">
          <span className="checkout-progress-dot">3</span>
          <span>{t('checkout.stepConfirmation', 'Confirmation')}</span>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>!</span>
          <span>{error}</span>
        </div>
      )}

      <div className="checkout-layout">
        {/* Left Column - Form or Payment */}
        <div className="checkout-form-section">
          {checkoutStep === 'form' ? (
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h2>{t('checkout.customerInfo')}</h2>
                
                <div className="form-group">
                  <label htmlFor="name">{t('checkout.name')} *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={formErrors.name ? 'error' : ''}
                    aria-invalid={formErrors.name ? true : undefined}
                    placeholder="John Doe"
                  />
                  {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">
                      {t('checkout.email')} {whatsappNotifications ? <span className="checkout-optional-hint">({t('checkout.optional', 'optional')})</span> : '*'}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={formErrors.email ? 'error' : ''}
                      aria-invalid={formErrors.email ? true : undefined}
                      placeholder="john@example.com"
                    />
                    {formErrors.email && <span className="error-message">{formErrors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">{t('checkout.phone')} *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={formErrors.phone ? 'error' : ''}
                      aria-invalid={formErrors.phone ? true : undefined}
                      placeholder={`${storeDefaultPhonePrefix || '+1'} XXX XXX XXXX`}
                    />
                    {formErrors.phone && <span className="error-message">{formErrors.phone}</span>}
                  </div>
                </div>

                {whatsappNotifications && !formData.email.trim() && (
                  <div className="form-group checkout-terms-group checkout-whatsapp-consent">
                    <label className="checkout-terms-label">
                      <input
                        type="checkbox"
                        id="whatsappConsent"
                        checked={whatsappConsent}
                        onChange={(e) => {
                          setWhatsappConsent(e.target.checked);
                          if (e.target.checked) {
                            setFormErrors(prev => ({ ...prev, whatsappConsent: undefined }));
                          }
                        }}
                      />
                      <span className="checkout-terms-text">
                        {t('checkout.whatsappConsent', 'Notify me on WhatsApp about my order')}
                      </span>
                    </label>
                    <span className="checkout-whatsapp-consent-hint">
                      {t('checkout.whatsappConsentHint', 'No email? Order updates will arrive on WhatsApp at the phone number above.')}
                    </span>
                    {formErrors.whatsappConsent && (
                      <span className="error-message checkout-terms-error" role="alert">
                        {formErrors.whatsappConsent}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="form-section">
                <h2>{t('checkout.deliveryAddress')}</h2>

                <div className="checkout__detect-location-wrap">
                    <button
                      type="button"
                      className="checkout__detect-location-btn"
                      onClick={handleDetectLocation}
                      disabled={detectingLocation}
                    >
                      {detectingLocation ? (
                        <>
                          <svg className="checkout__detect-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10" opacity=".25"/><path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/></svg>
                          {t('checkout.detectingLocation', 'Detecting…')}
                        </>
                      ) : (
                        <>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg>
                          {t('checkout.detectMyLocation', 'Detect my address')}
                        </>
                      )}
                    </button>
                    {locationDetectError && (
                      <p className="checkout__detect-error">{locationDetectError}</p>
                    )}
                </div>

                <div className="form-group">
                  <label htmlFor="street">{t('checkout.street')} *</label>
                  <input
                    type="text"
                    id="street"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    className={formErrors.street ? 'error' : ''}
                    aria-invalid={formErrors.street ? true : undefined}
                    placeholder="123 Main Street, Apt 4B"
                  />
                  {formErrors.street && <span className="error-message">{formErrors.street}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">{t('checkout.city')} *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={formErrors.city ? 'error' : ''}
                      aria-invalid={formErrors.city ? true : undefined}
                      placeholder={t('checkout.cityPlaceholder')}
                    />
                    {formErrors.city && <span className="error-message">{formErrors.city}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="postalCode">{t('checkout.postalCode')} *</label>
                    <input
                      type="text"
                      id="postalCode"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className={formErrors.postalCode ? 'error' : ''}
                      aria-invalid={formErrors.postalCode ? true : undefined}
                      placeholder="1001"
                    />
                    {formErrors.postalCode && <span className="error-message">{formErrors.postalCode}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="country">{t('checkout.country')} *</label>
                  <select
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className={formErrors.country ? 'error' : ''}
                    aria-invalid={formErrors.country ? true : undefined}
                  >
                    {deliveryCountries?.length > 0 ? (
                      // Only where the store delivers (Search & sharing → areas served).
                      deliveryCountries.map((name) => <option key={name} value={name}>{name}</option>)
                    ) : (
                      <>
                        <option value="United States">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Australia">Australia</option>
                        <optgroup label="Europe">
                          <option value="Albania">Albania</option>
                          <option value="Austria">Austria</option>
                          <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                          <option value="Bulgaria">Bulgaria</option>
                          <option value="Croatia">Croatia</option>
                          <option value="France">France</option>
                          <option value="Germany">Germany</option>
                          <option value="Greece">Greece</option>
                          <option value="Italy">Italy</option>
                          <option value="Kosovo">Kosovo</option>
                          <option value="Montenegro">Montenegro</option>
                          <option value="North Macedonia">North Macedonia</option>
                          <option value="Romania">Romania</option>
                          <option value="Serbia">Serbia</option>
                          <option value="Slovenia">Slovenia</option>
                          <option value="Spain">Spain</option>
                          <option value="Turkey">Turkey</option>
                        </optgroup>
                        <option value="Other">Other</option>
                      </>
                    )}
                  </select>
                  {formErrors.country && <span className="error-message">{formErrors.country}</span>}
                </div>

                <LocationPicker
                  externalPosition={
                    formData.latitude && formData.longitude
                      ? { lat: formData.latitude, lng: formData.longitude }
                      : null
                  }
                  onChange={(pos, reverseData) => {
                    setFormData(prev => ({
                      ...prev,
                      latitude: pos?.lat || null,
                      longitude: pos?.lng || null,
                      ...(reverseData?.street && !prev.street ? { street: reverseData.street } : {}),
                      ...(reverseData?.city && !prev.city ? { city: reverseData.city } : {}),
                      ...(reverseData?.postalCode && !prev.postalCode ? { postalCode: reverseData.postalCode } : {}),
                    }));
                  }}
                />
              </div>

              <div className="form-section">
                <h2>{t('checkout.notes')}</h2>
                <div className="form-group">
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder={t('checkout.notes')}
                    rows="4"
                  />
                </div>
              </div>

              <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              {paymentMethods.length > 1 && (
                <fieldset className="form-section checkout-payment">
                  <legend><h2>{t('checkout.payment.title', 'Payment')}</h2></legend>
                  {paymentMethods.map((method) => (
                    <label key={method} className={`checkout-payment-option${method === paymentMethod ? ' is-selected' : ''}`}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value={method}
                        checked={method === paymentMethod}
                        onChange={() => setPaymentChoice(method)}
                      />
                      <span className="checkout-payment-option__text">
                        <strong>{paymentLabel(method)}</strong>
                        <small>{paymentHint(method)}</small>
                      </span>
                    </label>
                  ))}
                  {paymentMethod === 'iutepay' && (
                    <>
                      <IuteInstalments amount={payableTotal} sku="cart" pageType="cart" className="checkout-payment__iute" />
                      <p className="checkout-payment__note">
                        {t('checkout.payment.iutepayNote', 'IutePay opens in a window here. Your order is placed once you submit the application, and we prepare it once IuteCredit approves.')}
                      </p>
                    </>
                  )}
                  {paymentMethod === 'bank_transfer' && (
                    <div className="checkout-payment__note">
                      <p>{t('payment.bankTransferMessage', "Transfer the order total to the account below, then confirm your order. We'll mark it paid once the transfer is received.")}</p>
                      {paymentConfig?.bankTransferBankName && <div><strong>{t('payment.bankName', 'Bank')}:</strong> {paymentConfig.bankTransferBankName}</div>}
                      {paymentConfig?.bankTransferAccountName && <div><strong>{t('payment.accountName', 'Account name')}:</strong> {paymentConfig.bankTransferAccountName}</div>}
                      {paymentConfig?.bankTransferAccountNumber && <div><strong>{t('payment.accountNumber', 'Account / IBAN')}:</strong> {paymentConfig.bankTransferAccountNumber}</div>}
                      {paymentConfig?.bankTransferInstructions && <div>{paymentConfig.bankTransferInstructions}</div>}
                    </div>
                  )}
                </fieldset>
              )}

              {belowMinOrder && (
                <div className="alert alert-warning" style={{marginBottom:'1rem',padding:'12px 16px',borderRadius:'8px',background:'#fff8e1',border:'1px solid #ffe082',color:'#6d4c00'}}>
                  {t('common.minOrderMessage', { amount: fmtPrice(minOrder), currency })}
                </div>
              )}

              <div className="form-group checkout-terms-group">
                <label className="checkout-terms-label">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    data-testid="checkout-terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => {
                      setTermsAccepted(e.target.checked);
                      if (e.target.checked) setTermsError(false);
                    }}
                  />
                  <span className="checkout-terms-text">
                    {/* <0>/<1> in the translation are these two links, in this order. */}
                    <Trans
                      i18nKey="checkout.termsLabel"
                      defaults="I agree to the <0>Terms & Conditions</0> and <1>Privacy Policy</1>."
                      components={[<Link to="/terms" key="terms" />, <Link to="/privacy" key="privacy" />]}
                    />
                  </span>
                </label>
                {termsError && (
                  <span className="error-message checkout-terms-error" role="alert">
                    {t('checkout.termsRequired', 'Please accept the Terms & Conditions and Privacy Policy to continue.')}
                  </span>
                )}
              </div>

              <div className="checkout-trust" aria-label={t('checkout.trust.label', 'Checkout reassurance')}>
                <span className="checkout-trust-item">
                  <Icon.Lock size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                  {t('checkout.trust.secure', 'Secure payment')}
                </span>
                {returnsEnabled && (
                  <span className="checkout-trust-item">
                    <Icon.Returns size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                    {t('checkout.trust.returns', { days: returnsWindowDays, defaultValue: `${returnsWindowDays}-day returns` })}
                  </span>
                )}
                <span className="checkout-trust-item">
                  <Icon.Shipping size={16} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
                  {t('checkout.trust.tracked', 'Tracked delivery')}
                </span>
              </div>

              {paymentError && (
                <div className="checkout-payment-error" role="alert">{paymentError}</div>
              )}

              <Button
                type="submit"
                variant="brand"
                size="lg"
                fullWidth
                disabled={loading || belowMinOrder || !termsAccepted}
                className="checkout-submit"
              >
                {loading
                  ? (paymentMethod === 'iutepay' ? t('checkout.iutepayOpen', 'Complete the application in the IutePay window…') : t('checkout.processing'))
                  : submitLabel}
              </Button>
            </form>
          ) : (
            <PaymentStep
              method={paymentMethod}
              orderId={createdOrder?.id}
              orderTotal={createdOrder?.total}
              onPaymentComplete={handlePaymentComplete}
              onBack={handlePaymentBack}
              paymentConfig={paymentConfig}
            />
          )}
        </div>

        {/* Right Column - Order Summary */}
        <div className="order-summary-section">
          <div className="order-summary-sticky">
            <h2>{t('checkout.orderSummary')}</h2>
            
            <div className="order-items">
              {cart.map(item => (
                <div key={item.id} className="order-item">
                  <div className="order-item-image">
                    {item.image ? (
                      <img src={getImageUrl(item.image, { size: 'thumb' })} alt={item.name} loading="lazy" onError={(e) => { if (!e.target.dataset.fallback) { e.target.dataset.fallback = '1'; e.target.src = getImageUrl(item.image); } }} />
                    ) : (
                      <div className="order-item-placeholder"></div>
                    )}
                  </div>
                  <div className="order-item-details">
                    <h4>{item.name}</h4>
                    <p>Qty: {item.quantity}</p>
                  </div>
                  <div className="order-item-price">
                    {fmtPrice(parseFloat(item.price) * item.quantity)} {currency}
                  </div>
                </div>
              ))}
            </div>

            {matchedBundles.length > 0 && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px' }}>
                <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#92400e', marginBottom: '0.375rem' }}>
                  {t('checkout.bundleSavings', 'Bundle Savings Applied!')}
                </div>
                {matchedBundles.map(b => (
                  <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.813rem', color: '#78350f', marginTop: '0.25rem' }}>
                    <span>{b.name}</span>
                    <span style={{ fontWeight: 600, color: '#059669' }}>-{fmtPrice(b.savings)} {currency}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Coupon Code */}
            <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '0.5rem' }}>
                {t('checkout.couponCode', 'Coupon Code')}
              </label>
              {automaticCoupon && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem', padding: '0.55rem 0.65rem', background: '#ecfdf5', color: '#065f46', borderRadius: 6, fontSize: '0.813rem' }}>
                  <strong>{couponResult ? 'Combined offer:' : 'Automatic offer:'}</strong> <span style={{ fontFamily: 'monospace' }}>{automaticCoupon.code}</span>
                  <span style={{ marginLeft: 'auto', fontWeight: 700 }}>-{fmtPrice(automaticCoupon.discount)} {currency}{automaticCoupon.freeShipping ? ' + free shipping' : ''}</span>
                </div>
              )}
              {couponResult ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ padding: '0.375rem 0.75rem', background: '#d1fae5', color: '#065f46', borderRadius: '6px', fontWeight: 600, fontSize: '0.875rem', fontFamily: 'monospace' }}>
                    {couponResult.code}
                  </span>
                  <span style={{ fontSize: '0.813rem', color: '#059669', fontWeight: 600 }}>
                    -{fmtPrice(couponResult.discount)} {currency}
                  </span>
                  <button onClick={handleRemoveCoupon} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}>
                    {t('common.remove', 'Remove')}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                    placeholder="SUMMER25"
                    style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', fontFamily: 'monospace', textTransform: 'uppercase' }}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleApplyCoupon())}
                  />
                  <button
                    onClick={handleApplyCoupon}
                    disabled={validatingCoupon || !couponCode.trim()}
                    style={{
                      padding: '0.5rem 1rem', background: '#4f46e5', color: 'white', border: 'none',
                      borderRadius: '6px', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer',
                      opacity: validatingCoupon || !couponCode.trim() ? 0.5 : 1,
                    }}
                  >
                    {validatingCoupon ? '...' : t('checkout.apply', 'Apply')}
                  </button>
                </div>
              )}
              {couponError && <p style={{ color: '#dc2626', fontSize: '0.813rem', marginTop: '0.375rem', fontWeight: 500 }}>{couponError}</p>}
            </div>

            {siteSettings.estimatedDeliveryDaysMin && siteSettings.estimatedDeliveryDaysMax && (
              <div style={{ padding: '0.75rem 1rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.875rem', color: '#166534', fontWeight: 600 }}>
                {(() => {
                  const min = parseInt(siteSettings.estimatedDeliveryDaysMin, 10) || 3;
                  const max = parseInt(siteSettings.estimatedDeliveryDaysMax, 10) || 5;
                  const fmt = { month: 'short', day: 'numeric' };
                  const minDate = new Date();
                  minDate.setDate(minDate.getDate() + min);
                  const maxDate = new Date();
                  maxDate.setDate(maxDate.getDate() + max);
                  return `Estimated delivery: ${minDate.toLocaleDateString('en-US', fmt)} - ${maxDate.toLocaleDateString('en-US', fmt)}`;
                })()}
              </div>
            )}

            <div className="order-totals">
              <div className="total-row">
                <span>{t('common.subtotal')}:</span>
                <span>{fmtPrice(subtotal)} {currency}</span>
              </div>
              {discountAmount > 0 && (
                <div className="total-row" style={{ color: '#059669' }}>
                  <span>{t('checkout.discount', 'Discount')}:</span>
                  <span>-{fmtPrice(discountAmount)} {currency}</span>
                </div>
              )}
              <div className="total-row">
                <span>{t('common.shipping')}:</span>
                <span>{parseFloat(shippingCost) === 0 || couponResult?.freeShipping || automaticCoupon?.freeShipping ? t('common.freeShipping', 'Free') : `${fmtPrice(shippingCost)} ${currency}`}</span>
              </div>
              <div className="total-row">
                <span>{taxLabel}:</span>
                <span>{fmtPrice(tax)} {currency}</span>
              </div>
              <div className="total-row total-final">
                <span>{t('common.total')}:</span>
                <span>{fmtPrice(discountAmount > 0 || waivedShipping > 0 ? adjustedTotal : total)} {currency}</span>
              </div>
              {cart.some(item => item.availabilityStatus === 'preorder') && (() => {
                const preorderSubtotal = cart
                  .filter(item => item.availabilityStatus === 'preorder')
                  .reduce((sum, item) => sum + parseFloat(item.price) * item.quantity, 0);
                const depositEstimate = Math.round(preorderSubtotal * 0.3);
                const finalTotal = discountAmount > 0 ? adjustedTotal : parseFloat(total);
                const balanceEstimate = Math.round(finalTotal - depositEstimate);
                return (
                  <div className="preorder-deposit-info" style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '0.85rem', color: '#1e40af' }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.375rem' }}>{t('checkout.preorderDeposit', 'Pre-order Deposit')}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{t('checkout.depositDueNow', 'Deposit due now')}:</span>
                      <span style={{ fontWeight: 600 }}>~{fmtPrice(depositEstimate)} {currency}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                      <span>{t('checkout.balanceAtShipping', 'Balance due at shipping')}:</span>
                      <span style={{ fontWeight: 600 }}>~{fmtPrice(balanceEstimate)} {currency}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="secure-checkout-badge">
              <Icon.Lock size={14} strokeWidth={ICON_DEFAULTS.strokeWidth} aria-hidden="true" />
              {t('checkout.secureCheckout')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
